/**
 * 文档历史钩子
 * 处理文档历史记录的业务逻辑
 */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
// 修正：导入正确的TextDocumentStorageProvider (from package root)
import { TextDocumentStorageProvider, PdfDocumentStorageProvider } from '@readsavor/utils';
// 修正：使用正确的路径导入useDocument
import { useDocument } from './useDocument.js';
import { useLogger } from '../../logging/hooks/index.js'; // Import useLogger
/**
 * 文档历史钩子函数 (重构)
 * 管理文档历史列表的业务逻辑，支持分页和搜索
 *
 * @param options 配置选项
 * @returns UseDocumentHistoryResult 包含文档数据和操作方法
 */
export function useDocumentHistory(options = {}) {
    // --- 默认选项 ---
    const { searchText = '', sortBy = 'timestamp-desc', limit = 20, page: initialPage = 1 } = options;
    // --- State ---
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(initialPage);
    const [hasMore, setHasMore] = useState(false);
    const [totalDocumentCount, setTotalDocumentCount] = useState(0); // Added state for total count
    const [filteredCount, setFilteredCount] = useState(0); // Added filteredCount state
    // --- Refs for stable dependencies ---
    const optionsRef = useRef({ searchText, sortBy, limit });
    const pageRef = useRef(page);
    // Update refs whenever options/page change
    useEffect(() => {
        optionsRef.current = { searchText, sortBy, limit };
    }, [searchText, sortBy, limit]);
    useEffect(() => {
        pageRef.current = page;
    }, [page]);
    // --- Dependencies ---
    const textStorageProvider = useMemo(() => TextDocumentStorageProvider.getInstance(), []);
    const pdfStorageProvider = useMemo(() => PdfDocumentStorageProvider.getInstance(), []);
    const { deleteDocument: deleteDocFromContext } = useDocument();
    const logger = useLogger();
    // --- Helper Functions ---
    const convertTextDocToHistoryItem = useCallback((doc) => {
        const docIdToUse = doc.id === undefined || doc.id === null ? 0 : doc.id;
        return {
            identity: { id: docIdToUse, type: 'text' },
            title: doc.title || 'Untitled Document',
            preview: doc.preview || '',
            timestamp: doc.timestamp || Date.now(),
            language: doc.language || 'unknown',
            targetLanguage: doc.targetLanguage || 'en',
        };
    }, []);
    const convertPdfDocToHistoryItem = useCallback((doc) => {
        const docIdToUse = doc.id === undefined || doc.id === null ? 0 : doc.id;
        return {
            identity: { id: docIdToUse, type: 'pdf' },
            title: doc.title || 'Untitled Document',
            preview: doc.filename || 'PDF Document',
            timestamp: doc.timestamp || Date.now(),
            language: doc.language || 'unknown',
            targetLanguage: doc.targetLanguage || 'en',
        };
    }, []);
    // --- Core Loading Logic ---
    const loadDocumentsInternal = useCallback(async (isNewQuery = false) => {
        const currentOptions = optionsRef.current;
        const currentPage = isNewQuery ? 1 : pageRef.current;
        // 如果是新查询且当前页不是第一页，则通过设置 page=1 来触发重新加载
        if (isNewQuery && pageRef.current !== 1) {
            setPage(1);
            return;
        }
        // 如果正在加载中，则不执行任何操作 (防止并发加载)
        // 注意：可能需要更复杂的逻辑来处理快速触发的情况
        // if (isLoading) return;
        setIsLoading(true);
        setError(null);
        try {
            // Options for provider calls (no pagination params from hook's current page yet, get all matching search/sort)
            const providerQueryOptions = {
                searchText: currentOptions.searchText,
                sortBy: currentOptions.sortBy,
                // We fetch all matching items from providers, then paginate the combined list in this hook.
                // So, we don't pass currentOptions.limit or a calculated offset to the providers here.
            };
            const [textDocsFromProvider, pdfDocsFromProvider] = await Promise.all([
                textStorageProvider.getDocuments(providerQueryOptions),
                pdfStorageProvider.getDocuments(providerQueryOptions),
            ]);
            const textHistoryItems = textDocsFromProvider.map(convertTextDocToHistoryItem);
            const pdfHistoryItems = pdfDocsFromProvider.map(convertPdfDocToHistoryItem);
            const combinedHistoryItems = [...textHistoryItems, ...pdfHistoryItems];
            // Sort the combined list (providers might have sorted internally, but this ensures overall sort)
            if (currentOptions.sortBy === 'time-asc') {
                combinedHistoryItems.sort((a, b) => a.timestamp - b.timestamp);
            }
            else {
                // Default 'timestamp-desc'
                combinedHistoryItems.sort((a, b) => b.timestamp - a.timestamp);
            }
            // Add other sorting logic if sortBy supports more than timestamp
            setFilteredCount(combinedHistoryItems.length); // Total items matching filter, before pagination
            // Apply pagination to the combined, sorted list
            const offset = (currentPage - 1) * currentOptions.limit;
            const paginatedItems = combinedHistoryItems.slice(offset, offset + currentOptions.limit);
            setDocuments(currentPage === 1 ? paginatedItems : [...documents, ...paginatedItems]);
            setHasMore(offset + paginatedItems.length < combinedHistoryItems.length);
            // Fetch and set total count ONLY when loading the first page AND no search filter is active
            if (currentPage === 1 && !currentOptions.searchText) {
                try {
                    const [textTotal, pdfTotal] = await Promise.all([
                        textStorageProvider.getDocumentCount(),
                        pdfStorageProvider.getDocumentCount(),
                    ]);
                    setTotalDocumentCount(textTotal + pdfTotal);
                }
                catch (countError) {
                    logger.error('Error fetching total document counts', { error: countError });
                    setTotalDocumentCount(0); // Default to 0 on error
                }
            }
            else if (currentPage === 1 && currentOptions.searchText) {
                // If there's a search text, totalDocumentCount (grand total) might not be what's displayed
                // as "total". filteredCount is more relevant. We could still fetch grand total if needed.
                // For now, ensure totalDocumentCount is fetched if it hasn't been.
                if (totalDocumentCount === 0) {
                    // Fetch grand total if not already fetched
                    try {
                        const [textTotal, pdfTotal] = await Promise.all([
                            textStorageProvider.getDocumentCount(),
                            pdfStorageProvider.getDocumentCount(),
                        ]);
                        setTotalDocumentCount(textTotal + pdfTotal);
                    }
                    catch (countError) {
                        logger.error('Error fetching total document counts during search', {
                            error: countError,
                        });
                        // Potentially set totalDocumentCount to a specific state or leave as is
                    }
                }
            }
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(`Failed to load history: ${errorMessage}`);
        }
        finally {
            setIsLoading(false);
        }
    }, [
        textStorageProvider,
        pdfStorageProvider,
        convertTextDocToHistoryItem,
        convertPdfDocToHistoryItem,
        documents,
        logger,
        totalDocumentCount,
    ]);
    // 使用 ref 存储最新的加载函数引用，避免 useEffect 依赖问题
    const loadDocumentsRef = useRef(loadDocumentsInternal);
    useEffect(() => {
        loadDocumentsRef.current = loadDocumentsInternal;
    }, [loadDocumentsInternal]);
    // --- Effects for loading data ---
    // 首次挂载时加载
    useEffect(() => {
        loadDocumentsRef.current(true); // true 表示是新查询/初始加载
    }, [logger]); // 添加 logger 依赖，移除 eslint-disable
    // 当搜索文本或排序方式变化时，重新从第一页加载
    useEffect(() => {
        loadDocumentsRef.current(true);
    }, [searchText, sortBy, logger]); // 添加 logger 依赖
    // 当页码变化时加载更多 (但跳过初始页码，因为初始加载已由其他 effect 处理)
    useEffect(() => {
        // 只有当页码大于初始页码时才执行加载更多
        if (page > initialPage) {
            loadDocumentsRef.current(false);
        }
    }, [page, initialPage, logger]); // 添加 logger 依赖，移除 eslint-disable
    // --- Exported Functions ---
    // 加载更多
    const loadMore = useCallback(() => {
        if (!isLoading && hasMore) {
            setPage(prev => prev + 1);
        }
    }, [isLoading, hasMore]); // 移除 logger 依赖
    // 刷新数据 (回到第一页并重新加载)
    const refreshDocuments = useCallback(() => {
        if (pageRef.current !== 1) {
            setPage(1);
        }
        else {
            // If already on page 1, force reload
            loadDocumentsRef.current(true);
        }
    }, []); // 移除 logger 依赖
    // 重置状态
    const resetHistory = useCallback(() => {
        setDocuments([]);
        setTotalDocumentCount(0);
        setFilteredCount(0);
        setHasMore(false);
        setPage(1);
        setIsLoading(false);
        setError(null);
    }, []);
    // 删除文档
    const deleteDocument = useCallback(async (identity, docTitle) => {
        const confirmed = window.confirm(`Are you sure you want to delete the document "${docTitle}"?\nRelated translations will also be deleted.`);
        if (!confirmed)
            return false;
        setIsLoading(true); // Optional: indicate loading during delete
        try {
            let deletedFromProvider = false;
            if (identity.type === 'text') {
                await deleteDocFromContext(identity); // Assuming this is still relevant for text
                await textStorageProvider.deleteDocument(identity);
                deletedFromProvider = true;
            }
            else if (identity.type === 'pdf') {
                // If there's a PDF-specific context deletion, call it here
                // e.g., await deletePdfDocFromContext(identity);
                await pdfStorageProvider.deleteDocument(identity);
                deletedFromProvider = true;
            }
            else {
                logger.error('Attempted to delete document with unknown type', { identity });
                throw new Error('Unknown document type for deletion');
            }
            if (deletedFromProvider) {
                // Update local state
                setDocuments(prevDocs => prevDocs.filter(doc => !(doc.identity.id === identity.id && doc.identity.type === identity.type)));
                // Adjust counts
                setTotalDocumentCount(prev => prev - 1);
                // If searchText is active, filteredCount should also be decremented
                // This assumes the deleted item was part of the current filter.
                // A more robust way for filteredCount might be to re-evaluate it or ensure consistency.
                // For simplicity, if it's in the current `documents` list, it was part of filtered results.
                if (optionsRef.current.searchText) {
                    setFilteredCount(prev => prev - 1);
                }
                logger.info(`Document ${identity.id} (${identity.type}) deleted successfully from history.`, { hook: 'useDocumentHistory' });
                return true;
            }
            return false; // Should not happen if type is known
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            logger.error(`Error deleting document ${identity.id} (${identity.type}): ${errorMessage}`, {
                hook: 'useDocumentHistory',
                error: err,
            });
            setError(`Deletion failed: ${errorMessage}`);
            return false;
        }
    }, [deleteDocFromContext, textStorageProvider, pdfStorageProvider, logger]);
    // --- Return Value ---
    return {
        documents,
        isLoading,
        error,
        hasMore,
        loadMore,
        refreshDocuments,
        deleteDocument,
        totalDocumentCount, // Added total count to return value
        filteredCount, // Added filteredCount to return value
        resetHistory,
    };
}
//# sourceMappingURL=useDocumentHistory.js.map